import 'core-js'
import 'css.escape'

import { isEmpty, isNil } from 'lodash'
import React, { useEffect, Suspense, useMemo, useRef } from 'react'
import { RecoilEnv, RecoilRoot, useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'
import { createStore as jotaiCreateStore, Provider as JotaiProvider } from 'jotai'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { sanitizeError } from 'src/helpers/sanitizeError'
import { useRunAnalysis } from 'src/hooks/useRunAnalysis'
import { useRunSeqAutodetectAsync } from 'src/hooks/useRunSeqAutodetect'
import i18nAuspice, { changeAuspiceLocale } from 'src/i18n/i18n.auspice'
import { isQueryParamTruthy } from 'src/io/getQueryParamMaybe'
import { loadInputs } from 'src/io/loadInputs'
import { mdxComponents } from 'src/mdx-components'
import LoadingPage from 'src/pages/loading'
import { globalErrorAtom } from 'src/state/error.state'
import {
  datasetJsonAtom,
  geneMapInputAtom,
  qrySeqInputsStorageAtom,
  refSeqInputAtom,
  refTreeInputAtom,
  virusPropertiesInputAtom,
} from 'src/state/inputs.state'
import { localeAtom } from 'src/state/locale.state'
import { isInitializedAtom } from 'src/state/settings.state'
import { ThemeProvider } from 'styled-components'
import { I18nextProvider } from 'react-i18next'
import { MDXProvider } from '@mdx-js/react'
import { QueryClient, QueryClientConfig, QueryClientProvider } from '@tanstack/react-query'
const DevToolsDrawer =
  process.env.NODE_ENV === 'development'
    ? dynamic(() => import('src/components/DevTools/DevToolsDrawer').then((mod) => mod.DevToolsDrawer), { ssr: false })
    : () => null
import { HelmetProvider } from 'react-helmet-async'
import { DOMAIN_STRIPPED } from 'src/constants'
import { parseUrl } from 'src/helpers/parseUrl'
import { getDatasetServerUrl, initializeDatasets } from 'src/io/fetchDatasets'
import { fetchSingleDataset } from 'src/io/fetchSingleDataset'
import { ErrorPopup } from 'src/components/Error/ErrorPopup'
import { SEO } from 'src/components/Common/SEO'
import { Plausible } from 'src/components/Common/Plausible'
import i18n, { changeLocale, getLocaleWithKey } from 'src/i18n/i18n'
import { theme } from 'src/theme'
import {
  datasetsAtom,
  allDatasetsAtom,
  datasetServerUrlAtom,
  minimizerIndexVersionAtom,
  datasetSelectionAtom,
  createDatasetSelection,
  collectionsAtom,
  type DatasetSelection,
} from 'src/state/dataset.state'
import { findDatasetByPath, findDatasetByPathAndTag } from 'src/helpers/sortDatasetVersions'
import { ErrorBoundary } from 'src/components/Error/ErrorBoundary'

import 'src/styles/global.scss'

RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false

/**
 * Dummy component that allows to set recoil state asynchronously. Needed because RecoilRoot's initializeState
 * currently only handles synchronous update and any calls to set() from promises have no effect
 */
function RecoilStateInitializer() {
  const router = useRouter()

  // NOTE: Do manual parsing, because router.query is randomly empty on the first few renders and on repeated renders.
  // This is important, because various states depend on query, and when it changes back and forth,
  // the state also changes unexpectedly.
  const { query: urlQuery } = useMemo(() => parseUrl(router.asPath), [router.asPath])

  const [initialized, setInitialized] = useRecoilState(isInitializedAtom)

  // Set synchronously when the initialization starts. The `initialized` state is set only after the asynchronous
  // steps, so a repeated effect run (React strict mode runs effects twice in development) would start a second
  // initialization, with duplicate data fetches and a second analysis run.
  const initializationStarted = useRef(false)

  const run = useRunAnalysis()
  const suggest = useRunSeqAutodetectAsync()

  const error = useRecoilValue(globalErrorAtom)

  const initialize = useRecoilCallback(({ set, snapshot }) => () => {
    if (initialized || initializationStarted.current) {
      return
    }
    initializationStarted.current = true

    const snapShotRelease = snapshot.retain()
    const { getPromise } = snapshot

    // eslint-disable-next-line no-void
    void Promise.resolve()
      // eslint-disable-next-line promise/always-return
      .then(async () => {
        const localeKey = await getPromise(localeAtom)
        const locale = getLocaleWithKey(localeKey)
        await changeLocale(i18n, locale.key)
        await changeAuspiceLocale(i18nAuspice, locale.key)
        set(localeAtom, locale.key)
      })
      .then(async () => {
        const datasetInfo = await fetchSingleDataset(urlQuery)

        if (!isNil(datasetInfo)) {
          set(datasetServerUrlAtom, undefined)
          const { datasets, currentDataset, auspiceJson } = datasetInfo
          return {
            datasets,
            allDatasets: datasets,
            currentDataset,
            minimizerIndexVersion: undefined,
            auspiceJson,
            datasetServerUrl: undefined,
          }
        }

        const datasetServerUrl = await getDatasetServerUrl(urlQuery)

        const { datasets, allDatasets, currentDataset, minimizerIndexVersion, collections } = await initializeDatasets(
          datasetServerUrl,
          urlQuery,
        )

        set(datasetServerUrlAtom, datasetServerUrl)
        return {
          datasets,
          allDatasets,
          currentDataset,
          minimizerIndexVersion,
          collections,
          auspiceJson: undefined,
          datasetServerUrl,
        }
      })
      .catch((error) => {
        // Dataset error is fatal and we want error to be handled in the ErrorBoundary
        setInitialized(false)
        set(globalErrorAtom, sanitizeError(error))
        throw error
      })
      .then(
        async ({
          datasets,
          allDatasets,
          currentDataset,
          minimizerIndexVersion,
          collections,
          auspiceJson,
          datasetServerUrl,
        }) => {
          set(datasetsAtom, datasets)
          set(allDatasetsAtom, allDatasets)
          set(collectionsAtom, collections ?? {})

          // Handle dataset selection with new architecture
          let resolvedDataset = currentDataset

          // If there's a current dataset from URL params, save it to the new selection system
          if (currentDataset) {
            // For datasets loaded from dataset-url (custom datasets), they don't have version/tag
            // In this case, we need to manually create a selection with the path
            if (!currentDataset.version?.tag) {
              // For custom datasets, use the path as a unique identifier
              // We don't persist these to localStorage (serverUrl undefined means they won't persist)
              const selection: DatasetSelection = {
                path: currentDataset.path,
                tag: undefined, // No tag for custom datasets
              }
              set(datasetSelectionAtom, selection)
              resolvedDataset = currentDataset
            } else {
              // For datasets with tags (from dataset servers)
              const serverUrl = datasetServerUrl ?? process.env.DATA_FULL_DOMAIN ?? '/'
              const selection = createDatasetSelection(currentDataset, serverUrl, allDatasets)
              if (selection) {
                set(datasetSelectionAtom, selection)
              }
              resolvedDataset = currentDataset
            }
          }
          // Otherwise, try to resolve from the new persisted selection
          else {
            const currentSelection = await getPromise(datasetSelectionAtom)

            // If persisted selection is from default server (no serverUrl or matches default), restore it
            if (currentSelection && allDatasets) {
              const defaultServerUrl = process.env.DATA_FULL_DOMAIN ?? '/'
              const isFromDefaultServer = !currentSelection.serverUrl || currentSelection.serverUrl === defaultServerUrl

              if (isFromDefaultServer) {
                // If no tag is stored (undefined), use the latest version
                resolvedDataset = !currentSelection.tag
                  ? findDatasetByPath(datasets, currentSelection.path)
                  : findDatasetByPathAndTag(allDatasets, currentSelection.path, currentSelection.tag)
              } else {
                // Clear non-default server selections from localStorage
                set(datasetSelectionAtom, undefined)
              }
            }
          }

          set(minimizerIndexVersionAtom, minimizerIndexVersion)

          if (resolvedDataset?.type === 'auspiceJson' && !isNil(auspiceJson)) {
            set(datasetJsonAtom, auspiceJson)
          } else {
            set(datasetJsonAtom, undefined)
          }
          return resolvedDataset
        },
      )
      .then(async (dataset) => {
        const isMultiDataset = isQueryParamTruthy(urlQuery, 'multi-dataset')

        const { inputFastas, refSeq, geneMap, refTree, virusProperties } = await loadInputs(urlQuery, dataset)
        set(refSeqInputAtom, refSeq)
        set(geneMapInputAtom, geneMap)
        set(refTreeInputAtom, refTree)
        set(virusPropertiesInputAtom, virusProperties)

        if (!isEmpty(inputFastas)) {
          set(qrySeqInputsStorageAtom, inputFastas)
          if (isMultiDataset) {
            const suggestionResults = await suggest()
            if (suggestionResults && !isEmpty(suggestionResults.suggestions)) {
              run({ isSingle: false })
            }
          } else if (!isEmpty(dataset)) {
            run({ isSingle: true })
          }
        }

        return undefined
      })
      .then(() => {
        setInitialized(true)
        return undefined
      })
      .catch((error) => {
        setInitialized(true)
        set(globalErrorAtom, sanitizeError(error))
      })
      .finally(() => {
        snapShotRelease()
      })
  })

  useEffect(() => {
    initialize()

    // Empty deps: run on mount only. `initialize()` ignores repeated runs, such as the second effect run of strict mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!initialized && !isNil(error)) {
    throw error
  }

  return null
}

const REACT_QUERY_OPTIONS: QueryClientConfig = {
  defaultOptions: { queries: { retry: 1 } },
}

export function MyApp({ Component, pageProps, router }: AppProps) {
  const jotaiStore = useMemo(() => jotaiCreateStore(), [])
  const queryClient = useMemo(() => new QueryClient(REACT_QUERY_OPTIONS), [])
  const fallback = useMemo(() => <LoadingPage />, [])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' && !['/', '/loading'].includes(router.pathname)) {
      void router.replace('/') // eslint-disable-line no-void
    }

    void router.prefetch('/') // eslint-disable-line no-void
    void router.prefetch('/results') // eslint-disable-line no-void
  }, [router])

  return (
    <Suspense fallback={fallback}>
      <HelmetProvider>
        <RecoilRoot>
          <JotaiProvider store={jotaiStore}>
            <ThemeProvider theme={theme}>
              <MDXProvider components={mdxComponents}>
                <Plausible domain={DOMAIN_STRIPPED} />
                <QueryClientProvider client={queryClient}>
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <I18nextProvider i18n={i18n}>
                    <ErrorBoundary>
                      <Suspense>
                        <RecoilStateInitializer />
                      </Suspense>
                      <Suspense fallback={fallback}>
                        <SEO />
                        <Component {...pageProps} />
                        <ErrorPopup />
                        <DevToolsDrawer jotaiStore={jotaiStore} />
                      </Suspense>
                    </ErrorBoundary>
                  </I18nextProvider>
                </QueryClientProvider>
              </MDXProvider>
            </ThemeProvider>
          </JotaiProvider>
        </RecoilRoot>
      </HelmetProvider>
    </Suspense>
  )
}

// NOTE: This disables server-side rendering (SSR) entirely, to avoid fatal error
// 'Hydration failed because the initial UI does not match what was rendered on the server'.
// This is probably a bug in React 18, or in Next.js, or in the app. There's been reports that improper
// nesting of HTML and SVG elements can cause the mismatch.
//
// See:
//  - https://github.com/facebook/react/issues/22833
//  - https://github.com/facebook/react/issues/24519
//  - https://github.com/vercel/next.js/discussions/35773
//  - https://nextjs.org/docs/messages/react-hydration-error
//  - https://stackoverflow.com/questions/71706064/react-18-hydration-failed-because-the-initial-ui-does-not-match-what-was-render
//
//
// NOTE: <Suspense /> does not seem to be working properly when SSR is enabled. The server hangs forever
// on the first unresolved Recoil atom, i.e. an atom without a default value. Such atoms, if accessed
// before they are initialized, trigger Suspense. However the server hangs, failing to make any progress past this
// point, even if this atom is initialized shortly after by another component. One example is the dataset atom. It is
// initially in suspended state, and unlocks after the dataset is fetched. However server stops at the dataset selector
// component and does not proceed further. We might not be handling the initialization of Recoil atoms properly,
// or it is simply not yet implemented well in Next.js or Recoil.
//
//
// TODO: When the hydration error, and the loading of Recoil atoms are sorted out we may want to remove this line,
// exporting the app component directly, reenabling SSR.
export default dynamic(() => Promise.resolve(MyApp), { ssr: false })
